import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import {
  readWishlist,
  WISHLIST_CHANGE_EVENT,
  WISHLIST_STORAGE_KEY,
  writeWishlist,
} from '@theme/wishlist-button';

/**
 * @typedef {object} WishlistProductVariant
 * @property {number | string} id
 * @property {number} price
 * @property {boolean} available
 * @property {string} title
 * @property {string} [public_title]
 */

/**
 * @typedef {object} WishlistProduct
 * @property {string} handle
 * @property {string} title
 * @property {number} price
 * @property {string | null} [featured_image]
 * @property {string[]} [images]
 * @property {WishlistProductVariant[]} variants
 */

/**
 * @param {HTMLElement} element
 * @param {string} key
 */
function requiredData(element, key) {
  const value = element.dataset[key];
  if (value === undefined) throw new Error(`Wishlist is missing the "${key}" data attribute.`);
  return value;
}

/**
 * @param {unknown} value
 * @returns {value is WishlistProductVariant}
 */
function isWishlistProductVariant(value) {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    (typeof value.id === 'number' || typeof value.id === 'string') &&
    'price' in value &&
    typeof value.price === 'number' &&
    'available' in value &&
    typeof value.available === 'boolean' &&
    'title' in value &&
    typeof value.title === 'string' &&
    (!('public_title' in value) || typeof value.public_title === 'string')
  );
}

/**
 * @param {unknown} value
 * @returns {value is WishlistProduct}
 */
function isWishlistProduct(value) {
  return (
    typeof value === 'object' &&
    value !== null &&
    'handle' in value &&
    typeof value.handle === 'string' &&
    'title' in value &&
    typeof value.title === 'string' &&
    'price' in value &&
    typeof value.price === 'number' &&
    'variants' in value &&
    Array.isArray(value.variants) &&
    value.variants.every(isWishlistProductVariant) &&
    (!('featured_image' in value) ||
      value.featured_image === null ||
      typeof value.featured_image === 'string') &&
    (!('images' in value) ||
      (Array.isArray(value.images) && value.images.every((image) => typeof image === 'string')))
  );
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} status - Loading, empty, and error feedback.
 * @property {HTMLElement} grid - Rendered favorite products.
 *
 * @extends {Component<Refs>}
 */
class WishlistSection extends Component {
  requiredRefs = ['status', 'grid'];

  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {AbortController | undefined} */
  #request;
  #renderVersion = 0;
  #focusAfterLoad = false;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();

    window.addEventListener('storage', this.#handleStorage, { signal: this.#listeners.signal });
    window.addEventListener(WISHLIST_CHANGE_EVENT, this.#handleWishlistChange, {
      signal: this.#listeners.signal,
    });
    this.refs.grid.addEventListener('click', this.#handleGridClick, {
      signal: this.#listeners.signal,
    });
    this.refs.grid.addEventListener('submit', this.#handleGridSubmit, {
      signal: this.#listeners.signal,
    });

    void this.#loadFavorites();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#request?.abort();
    super.disconnectedCallback();
  }

  /** @param {StorageEvent} event */
  #handleStorage = (event) => {
    if (event.key === WISHLIST_STORAGE_KEY) void this.#loadFavorites();
  };

  #handleWishlistChange = () => {
    void this.#loadFavorites();
  };

  /** @param {MouseEvent} event */
  #handleGridClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-wishlist-remove]');
    if (!(button instanceof HTMLButtonElement)) return;

    try {
      const id = requiredData(button, 'productId');
      const handle = requiredData(button, 'productHandle');
      const items = readWishlist();
      this.#focusAfterLoad = true;
      writeWishlist(items.filter((item) => item.id !== id && (!handle || item.handle !== handle)));
    } catch (error) {
      console.error('[wishlist] Could not remove this favorite:', error);
      this.refs.status.textContent = requiredData(this, 'storageError');
    }
  };

  /** @param {SubmitEvent} event */
  #handleGridSubmit = async (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const form = event.target;
    if (!form.matches('[data-wishlist-add-form]')) return;
    event.preventDefault();

    const submitButton = form.querySelector('button[type="submit"]');
    if (!(submitButton instanceof HTMLButtonElement) || submitButton.disabled) return;

    const formData = new FormData(form);
    const productId = formData.get('id');
    const quantity = Number(formData.get('quantity')) || 1;
    if (typeof productId !== 'string' || !productId) {
      this.refs.status.textContent = requiredData(this, 'addToCartError');
      console.error('[wishlist] Add-to-cart form is missing a variant ID.');
      return;
    }

    const eventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'standard-action',
        lines: [{ merchandiseId: productId, quantity }],
        promise: eventPromise.promise,
      })
    );

    submitButton.disabled = true;
    let addSucceeded = false;
    try {
      formData.delete('return_to');
      const response = await fetch(requiredData(this, 'cartAddAjaxUrl'), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        const message =
          typeof result.description === 'string'
            ? result.description
            : requiredData(this, 'addToCartError');
        throw new Error(message);
      }

      addSucceeded = true;
      const card = form.closest('.wishlist-drawer__card');
      const removeButton = card?.querySelector('[data-wishlist-remove]');
      let favoriteRemoved = false;
      if (removeButton instanceof HTMLButtonElement) {
        try {
          const favoriteId = requiredData(removeButton, 'productId');
          const handle = requiredData(removeButton, 'productHandle');
          this.#focusAfterLoad = true;
          writeWishlist(
            readWishlist().filter((item) => item.id !== favoriteId && item.handle !== handle)
          );
          favoriteRemoved = true;
        } catch (error) {
          console.error('[wishlist] Product was added, but could not remove its favorite:', error);
        }
      } else {
        console.error('[wishlist] Product was added, but its favorite entry could not be identified.');
      }
      this.refs.status.textContent = requiredData(
        this,
        favoriteRemoved ? 'addedToCartLabel' : 'storageError'
      );
    } catch (error) {
      eventPromise.reject(error);
      console.error('[wishlist] Could not add favorite to cart:', error);
      this.refs.status.textContent =
        error instanceof Error ? error.message : requiredData(this, 'addToCartError');
      this.dispatchEvent(
        new CartErrorEvent({
          error: this.refs.status.textContent,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      submitButton.disabled = false;
    }

    if (!addSucceeded) return;

    try {
      const cartResponse = await fetch(requiredData(this, 'cartAjaxUrl'), {
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) {
        throw new Error(`Cart request failed with status ${cartResponse.status}.`);
      }
      const cart = await cartResponse.json();
      eventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: 'wishlist-drawer', productId, itemCount: quantity, didError: false },
      });
    } catch (error) {
      eventPromise.reject(error);
      console.error('[wishlist] Product was added, but the cart could not be refreshed:', error);
    }
  };

  async #loadFavorites() {
    const version = ++this.#renderVersion;
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.refs.grid.replaceChildren();
    this.refs.grid.hidden = true;

    let items;
    try {
      items = readWishlist();
    } catch (error) {
      console.error('[wishlist] Could not read saved favorites:', error);
      this.refs.status.textContent = requiredData(this, 'storageError');
      return;
    }

    if (items.length === 0) {
      this.refs.status.textContent = requiredData(this, 'emptyMessage');
      this.#restoreFocus();
      return;
    }

    const loadableItems = items.filter((item) => item.handle);
    const legacyCount = items.length - loadableItems.length;

    if (loadableItems.length === 0) {
      this.refs.status.textContent = requiredData(this, 'legacyMessage');
      this.#restoreFocus();
      return;
    }

    this.refs.status.textContent = requiredData(this, 'loadingMessage');

    let failedCount = legacyCount;
    const batchSize = 6;

    for (let start = 0; start < loadableItems.length; start += batchSize) {
      const batch = loadableItems.slice(start, start + batchSize);
      const results = await Promise.allSettled(
        batch.map((item) => this.#fetchProduct(item.handle, request.signal))
      );

      if (version !== this.#renderVersion || request.signal.aborted) return;

      for (const [index, result] of results.entries()) {
        const item = batch[index];
        if (!item) throw new Error(`Wishlist product batch is missing item ${index}.`);

        if (result.status === 'fulfilled') {
          try {
            this.refs.grid.append(this.#createProductCard(result.value, item));
          } catch (error) {
            failedCount += 1;
            console.error(`[wishlist] Could not render favorite "${item.handle}":`, error);
          }
        } else if (result.reason?.name !== 'AbortError') {
          failedCount += 1;
          console.error(`[wishlist] Could not load favorite "${item.handle}":`, result.reason);
        }
      }

      this.refs.grid.hidden = this.refs.grid.childElementCount === 0;
    }

    this.refs.status.textContent =
      failedCount > 0
        ? requiredData(this, 'partialErrorMessage')
        : this.refs.grid.hidden
          ? requiredData(this, 'emptyMessage')
          : '';
    this.#restoreFocus();
  }

  #restoreFocus() {
    if (!this.#focusAfterLoad) return;

    this.#focusAfterLoad = false;
    const nextRemoveButton = this.refs.grid.querySelector('[data-wishlist-remove]');
    if (nextRemoveButton instanceof HTMLButtonElement) {
      nextRemoveButton.focus();
    } else {
      this.refs.status.focus();
    }
  }

  /**
   * @param {string} handle
   * @param {AbortSignal} signal
   * @returns {Promise<WishlistProduct>}
   */
  async #fetchProduct(handle, signal) {
    const root = window.Shopify?.routes?.root ?? '/';
    const productUrl = new URL(
      `products/${encodeURIComponent(handle)}.js`,
      new URL(root, window.location.origin)
    );
    const response = await fetch(productUrl, { signal });

    if (!response.ok) {
      throw new Error(`Product request failed with status ${response.status}.`);
    }

    /** @type {unknown} */
    const product = await response.json();
    if (!isWishlistProduct(product) || product.handle !== handle || product.variants.length === 0) {
      throw new Error('The product response is invalid.');
    }

    return product;
  }

  /**
   * @param {WishlistProduct} product
   * @param {{ id: string, handle: string }} item
   */
  #createProductCard(product, item) {
    const card = document.createElement('article');
    card.className = 'wishlist-drawer__card';

    const productUrl = new URL(
      `products/${encodeURIComponent(product.handle)}`,
      new URL(window.Shopify?.routes?.root ?? '/', window.location.origin)
    );
    const imageUrl = product.featured_image || product.images?.[0];
    const imageLink = document.createElement('a');
    imageLink.className = 'wishlist-drawer__image-link';
    imageLink.href = productUrl.href;
    imageLink.setAttribute(
      'aria-label',
      `${requiredData(this, 'viewProductLabel')}: ${product.title}`
    );

    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = product.title;
      image.width = 800;
      image.height = 1000;
      image.loading = 'lazy';
      image.decoding = 'async';
      imageLink.append(image);
    }

    const details = document.createElement('div');
    details.className = 'wishlist-drawer__details';

    const title = document.createElement('a');
    title.className = 'wishlist-drawer__title';
    title.href = productUrl.href;
    title.textContent = product.title;
    details.append(title);

    const availableVariants = product.variants.filter((variant) => variant.available);
    const variant = availableVariants[0] ?? product.variants[0];
    const price = document.createElement('p');
    price.className = 'wishlist-drawer__price';
    price.textContent = formatMoney(
      variant?.price ?? product.price,
      requiredData(this, 'moneyFormat'),
      requiredData(this, 'currency')
    );
    details.append(price);

    const form = document.createElement('form');
    form.className = 'wishlist-drawer__form';
    form.dataset.wishlistAddForm = '';
    form.method = 'post';
    form.action = requiredData(this, 'cartAddUrl');

    const variantId = document.createElement('input');
    variantId.type = 'hidden';
    variantId.name = 'id';
    variantId.value = String(variant?.id ?? '');
    form.append(variantId);

    if (availableVariants.length > 1) {
      const select = document.createElement('select');
      select.className = 'wishlist-drawer__variant';
      select.setAttribute(
        'aria-label',
        `${requiredData(this, 'variantLabel')}: ${product.title}`
      );

      for (const availableVariant of availableVariants) {
        const option = document.createElement('option');
        option.value = String(availableVariant.id);
        option.textContent = availableVariant.public_title || availableVariant.title;
        option.dataset.price = String(availableVariant.price);
        select.append(option);
      }

      select.addEventListener('change', () => {
        const selectedVariant = availableVariants.find(
          (availableVariant) => String(availableVariant.id) === select.value
        );
        if (!selectedVariant) return;

        variantId.value = String(selectedVariant.id);
        price.textContent = formatMoney(
          selectedVariant.price,
          requiredData(this, 'moneyFormat'),
          requiredData(this, 'currency')
        );
      });
      form.append(select);
    }

    const quantity = document.createElement('input');
    quantity.type = 'hidden';
    quantity.name = 'quantity';
    quantity.value = '1';
    form.append(quantity);

    const returnTo = document.createElement('input');
    returnTo.type = 'hidden';
    returnTo.name = 'return_to';
    returnTo.value = requiredData(this, 'cartUrl');
    form.append(returnTo);

    const addButton = document.createElement('button');
    addButton.className = 'button wishlist-drawer__add';
    addButton.type = 'submit';
    addButton.textContent = requiredData(this, 'addToCartLabel');
    addButton.disabled = availableVariants.length === 0;
    if (availableVariants.length === 0) {
      addButton.textContent = requiredData(this, 'soldOutLabel');
    }
    form.append(addButton);
    details.append(form);

    const removeButton = document.createElement('button');
    removeButton.className = 'wishlist-drawer__remove';
    removeButton.type = 'button';
    removeButton.dataset.wishlistRemove = '';
    removeButton.dataset.productId = item.id;
    removeButton.dataset.productHandle = item.handle;
    removeButton.textContent = requiredData(this, 'removeLabel');
    removeButton.setAttribute(
      'aria-label',
      `${requiredData(this, 'removeProductLabel')}: ${product.title}`
    );
    details.append(removeButton);

    card.append(imageLink, details);
    return card;
  }
}

if (!customElements.get('wishlist-section')) {
  customElements.define('wishlist-section', WishlistSection);
}
